import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/layout/layout';

export default function ConfidentialitePage() {
  return (
    <Layout>
      <Helmet>
        <title>Politique de confidentialité | recruteurs.io</title>
        <meta
          name="description"
          content="Politique de confidentialité de recruteurs.io : données collectées, finalités, droits RGPD et gestion des cookies."
        />
        <link rel="canonical" href="https://recruteurs.io/legal/confidentialite/" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="container-custom max-w-3xl py-16">
        <h1 className="text-4xl font-bold mb-10 text-neutral-900">Politique de confidentialité</h1>

        <p className="text-neutral-600 leading-relaxed mb-10">
          La présente politique de confidentialité décrit comment <strong>recruteurs.io</strong> (LOOCATME, 11 chemin du Mont Gibaou, 13260 Cassis, France) collecte, utilise et protège vos données personnelles, conformément au Règlement général sur la protection des données (RGPD – UE 2016/679) et à la loi Informatique et Libertés.
        </p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">1. Responsable du traitement</h2>
          <p className="text-neutral-600 leading-relaxed">
            LOOCATME<br />
            11 chemin du Mont Gibaou, 13260 Cassis, France<br />
            E-mail DPO / contact RGPD : <a href="mailto:contact@recruteurs.io" className="underline hover:text-[#d03484]">contact@recruteurs.io</a>
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">2. Données collectées</h2>
          <p className="text-neutral-600 leading-relaxed mb-3">Nous collectons les données suivantes selon les actions effectuées :</p>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Formulaire de contact / prise de rendez-vous :</strong> prénom, nom, e-mail, téléphone, société, poste, message.</li>
            <li><strong>Formulaire de candidature :</strong> prénom, nom, e-mail, téléphone, CV (fichier), lettre de motivation, informations professionnelles.</li>
            <li><strong>Création de compte :</strong> e-mail, mot de passe (haché), prénom, nom, rôle.</li>
            <li><strong>Données de navigation :</strong> adresse IP (anonymisée), pages visitées, durée de session (via analytics).</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">3. Finalités du traitement</h2>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li>Répondre à vos demandes de contact et prises de rendez-vous.</li>
            <li>Traiter les candidatures et mettre en relation candidats et entreprises.</li>
            <li>Gérer votre espace client / compte utilisateur.</li>
            <li>Améliorer nos services et le fonctionnement du site (analytics).</li>
            <li>Respecter nos obligations légales et contractuelles.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">4. Base légale</h2>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Exécution du contrat</strong> : gestion des comptes clients, traitement des missions de recrutement.</li>
            <li><strong>Intérêt légitime</strong> : amélioration du service, sécurité du site.</li>
            <li><strong>Consentement</strong> : envoi de communications marketing (si applicable), dépôt de cookies non essentiels.</li>
            <li><strong>Obligation légale</strong> : conservation des données comptables et contractuelles.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">5. Durée de conservation</h2>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Données de contact / candidature :</strong> 2 ans à compter du dernier contact, sauf opposition.</li>
            <li><strong>Données de compte utilisateur :</strong> durée de vie du compte + 1 an après fermeture.</li>
            <li><strong>Données de navigation (analytics) :</strong> 13 mois maximum.</li>
            <li><strong>Données contractuelles / comptables :</strong> 10 ans conformément aux obligations légales.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">6. Destinataires des données</h2>
          <p className="text-neutral-600 leading-relaxed mb-3">
            Vos données sont destinées aux équipes internes de recruteurs.io. Elles peuvent être transmises aux sous-traitants suivants, dans le cadre strict des finalités décrites :
          </p>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Supabase</strong> (hébergement base de données) — infrastructure EU disponible.</li>
            <li><strong>Netlify</strong> (hébergement site) — États-Unis, encadré par les clauses contractuelles types.</li>
            <li><strong>Resend</strong> (envoi d&apos;emails transactionnels) — notifications et confirmations liées aux formulaires (rendez-vous, candidatures, livres blancs).</li>
            <li><strong>Google Analytics 4</strong> (analytics) — données anonymisées.</li>
          </ul>
          <p className="text-neutral-600 leading-relaxed mt-3">
            Aucune donnée n'est vendue ni cédée à des tiers à des fins commerciales.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">7. Vos droits</h2>
          <p className="text-neutral-600 leading-relaxed mb-3">
            Conformément au RGPD, vous disposez des droits suivants :
          </p>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Droit d'accès :</strong> obtenir une copie des données vous concernant.</li>
            <li><strong>Droit de rectification :</strong> corriger des données inexactes ou incomplètes.</li>
            <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données dans les limites légales.</li>
            <li><strong>Droit à la limitation :</strong> restreindre temporairement le traitement de vos données.</li>
            <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré et lisible.</li>
            <li><strong>Droit d'opposition :</strong> vous opposer à un traitement basé sur l'intérêt légitime ou à des fins de prospection.</li>
          </ul>
          <p className="text-neutral-600 leading-relaxed mt-4">
            Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@recruteurs.io" className="underline hover:text-[#d03484]">contact@recruteurs.io</a>.<br />
            Vous pouvez également introduire une réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#d03484]">CNIL</a>.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">8. Cookies</h2>
          <p className="text-neutral-600 leading-relaxed mb-3">
            Le site utilise des cookies pour son fonctionnement et l'analyse d'audience :
          </p>
          <ul className="list-disc list-inside text-neutral-600 space-y-2 leading-relaxed">
            <li><strong>Cookies essentiels :</strong> session d'authentification (Supabase). Nécessaires au fonctionnement, pas de consentement requis.</li>
            <li><strong>Cookies analytics :</strong> mesure d'audience anonymisée. Déposés uniquement avec votre consentement.</li>
          </ul>
          <p className="text-neutral-600 leading-relaxed mt-3">
            Vous pouvez gérer vos préférences cookies via les paramètres de votre navigateur.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">9. Sécurité</h2>
          <p className="text-neutral-600 leading-relaxed">
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte ou altération : chiffrement en transit (HTTPS), Row Level Security (RLS) sur la base de données, accès restreint par rôle.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-neutral-800 mb-3">10. Modifications</h2>
          <p className="text-neutral-600 leading-relaxed">
            Nous nous réservons le droit de modifier cette politique à tout moment. La date de dernière mise à jour est indiquée en bas de page. En cas de modification substantielle, nous vous en informerons par e-mail si vous disposez d'un compte.
          </p>
        </section>

        <p className="text-sm text-neutral-400 mt-12">Dernière mise à jour : juillet 2026</p>
      </div>
    </Layout>
  );
}
